import {forwardRef} from '@chakra-ui/react';
import type {HTMLChakraProps, ThemingProps} from '@chakra-ui/system';
import {omitThemingProps} from '@chakra-ui/system';
import {ChakraTime} from '@linx-security/ui/utils/chakra-elements';
import formatISO from 'date-fns/formatISO';
import formatDate from 'date-fns/format';
import {useMemo} from 'react';

import {useTextStyleConfig} from '../Text/theme';

type FormatDateParams = Parameters<typeof formatDate>;

export type TimeProps = {
  date?: FormatDateParams[0] | string | null;
  /**
   * formatDate format string
   * @default `MMM dd', ' yyyy 'at' HH:mm`
   */
  format?: FormatDateParams[1];
  options?: FormatDateParams[2];
  fallback?: string;
} & HTMLChakraProps<'time'> &
  ThemingProps;

export const DEFAULT_TIME_FORMAT = `MMM dd', ' yyyy 'at' HH:mm`;

export const Time = forwardRef<TimeProps, 'time'>(({format, options, date, fallback, ...rest}, ref) => {
  const props = omitThemingProps(rest);
  const __css = useTextStyleConfig(rest);
  const {dateTime, value} = useMemo(() => {
    // Check if we can use getDateInstance
    if (!date) return {value: fallback};

    try {
      const value = typeof date === `string` ? new Date(date) : date;
      return {
        dateTime: formatISO(value),
        value: formatDate(value, format || DEFAULT_TIME_FORMAT, options),
      };
    } catch {
      return {value: `${date}`};
    }
  }, [date, format, options, fallback]);

  return (
    <ChakraTime __css={__css} dateTime={dateTime} {...props} ref={ref}>
      {value}
    </ChakraTime>
  );
});
